import React from 'react';
import { Target, TrendingUp, TrendingDown, Award, Percent, Zap } from 'lucide-react';
import { SectionHeader } from './SectionHeader';

// Signal Score Metrics Component for Channel Analytic
export function SignalMetrics({ metrics, channelType, dateRangeLabel, isLoading }) {
  if (isLoading) {
    return (
      <div className="bg-[hsl(var(--surface))] border border-[hsl(var(--border))] rounded-xl overflow-hidden">
        <SectionHeader 
          icon={Target} 
          title="Signal Performance" 
          subtitle={`Loading metrics...`} 
        />
        <div className="p-5 flex items-center justify-center h-32">
          <div className="animate-spin w-6 h-6 border-2 border-[#3B82F6] border-t-transparent rounded-full" />
        </div>
      </div>
    );
  }

  if (!metrics) {
    return (
      <div className="bg-[hsl(var(--surface))] border border-[hsl(var(--border))] rounded-xl overflow-hidden">
        <SectionHeader 
          icon={Target} 
          title="Signal Performance" 
          subtitle={`${channelType === 'public' ? 'Public Channel' : 'VIP Channel'} • ${dateRangeLabel}`} 
        />
        <div className="p-5 text-center text-[#6B7280]">
          <Target size={32} className="mx-auto mb-2 opacity-50" />
          <p className="text-sm">No signal data available</p>
          <p className="text-xs mt-1">Key in trades in Daily Data → Operation tab</p>
        </div>
      </div>
    );
  }

  const { total_signals, total_win, total_loss, total_breakeven, total_pips_win, total_pips_loss, net_pips, win_rate } = metrics;

  return (
    <div className="bg-[hsl(var(--surface))] border border-[hsl(var(--border))] rounded-xl overflow-hidden">
      <SectionHeader 
        icon={Target} 
        title="Signal Performance" 
        subtitle={`${channelType === 'public' ? 'Public Channel' : channelType === 'vip' ? 'VIP Channel' : 'All Channels'} • ${dateRangeLabel}`}
        action={
          <div className={`px-3 py-1 rounded-full text-xs font-semibold ${
            win_rate >= 70 ? 'bg-[#10B981]/20 text-[#10B981]' :
            win_rate >= 50 ? 'bg-[#F59E0B]/20 text-[#F59E0B]' :
            'bg-[#E63946]/20 text-[#E63946]'
          }`}>
            {win_rate}% Win Rate
          </div>
        }
      />
      
      <div className="p-5">
        {/* Main Stats Grid */}
        <div className="grid grid-cols-5 gap-4 mb-4">
          {/* Total Signals */}
          <div className="bg-[#3B82F6]/10 rounded-xl p-4 border border-[#3B82F6]/20 text-center">
            <div className="w-10 h-10 rounded-lg flex items-center justify-center bg-[#3B82F6]/20 mx-auto mb-2">
              <Zap size={20} className="text-[#3B82F6]" />
            </div>
            <p className="text-2xl font-bold text-[#3B82F6]">{total_signals}</p>
            <p className="text-[10px] uppercase tracking-wider text-[#6B7280] mt-1">Total Signals</p>
          </div>
          
          {/* Win */}
          <div className="bg-[#10B981]/10 rounded-xl p-4 border border-[#10B981]/20 text-center">
            <div className="w-10 h-10 rounded-lg flex items-center justify-center bg-[#10B981]/20 mx-auto mb-2">
              <TrendingUp size={20} className="text-[#10B981]" />
            </div>
            <p className="text-2xl font-bold text-[#10B981]">{total_win + total_breakeven}</p>
            <p className="text-[10px] uppercase tracking-wider text-[#6B7280] mt-1">Win (incl. BE)</p>
          </div>
          
          {/* Loss */}
          <div className="bg-[#E63946]/10 rounded-xl p-4 border border-[#E63946]/20 text-center">
            <div className="w-10 h-10 rounded-lg flex items-center justify-center bg-[#E63946]/20 mx-auto mb-2">
              <TrendingDown size={20} className="text-[#E63946]" />
            </div>
            <p className="text-2xl font-bold text-[#E63946]">{total_loss}</p>
            <p className="text-[10px] uppercase tracking-wider text-[#6B7280] mt-1">Loss</p>
          </div>
          
          {/* Pips Won */}
          <div className="bg-[#8B5CF6]/10 rounded-xl p-4 border border-[#8B5CF6]/20 text-center">
            <div className="w-10 h-10 rounded-lg flex items-center justify-center bg-[#8B5CF6]/20 mx-auto mb-2">
              <Award size={20} className="text-[#8B5CF6]" />
            </div>
            <p className={`text-2xl font-bold ${net_pips >= 0 ? 'text-[#10B981]' : 'text-[#E63946]'}`}>
              {net_pips >= 0 ? '+' : ''}{net_pips}
            </p>
            <p className="text-[10px] uppercase tracking-wider text-[#6B7280] mt-1">Net Pips</p>
          </div>
          
          {/* Win Rate */}
          <div className={`rounded-xl p-4 border text-center ${
            win_rate >= 70 ? 'bg-[#10B981]/10 border-[#10B981]/20' :
            win_rate >= 50 ? 'bg-[#F59E0B]/10 border-[#F59E0B]/20' :
            'bg-[#E63946]/10 border-[#E63946]/20'
          }`}>
            <div className={`w-10 h-10 rounded-lg flex items-center justify-center mx-auto mb-2 ${
              win_rate >= 70 ? 'bg-[#10B981]/20' :
              win_rate >= 50 ? 'bg-[#F59E0B]/20' :
              'bg-[#E63946]/20'
            }`}>
              <Percent size={20} className={
                win_rate >= 70 ? 'text-[#10B981]' :
                win_rate >= 50 ? 'text-[#F59E0B]' :
                'text-[#E63946]'
              } />
            </div>
            <p className={`text-2xl font-bold ${
              win_rate >= 70 ? 'text-[#10B981]' :
              win_rate >= 50 ? 'text-[#F59E0B]' :
              'text-[#E63946]'
            }`}>{win_rate}%</p>
            <p className="text-[10px] uppercase tracking-wider text-[#6B7280] mt-1">Win Rate</p>
          </div>
        </div>
        
        {/* Pips Breakdown */}
        <div className="grid grid-cols-2 gap-4">
          <div className="bg-[hsl(var(--surface-elevated))] rounded-lg p-4 border border-[hsl(var(--border))]">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm text-white font-medium">Pips Won</span>
              <span className="text-lg font-bold text-[#10B981]">+{total_pips_win}</span>
            </div>
            <div className="h-2 bg-[hsl(var(--border))] rounded-full overflow-hidden">
              <div 
                className="h-full bg-gradient-to-r from-[#10B981] to-[#34D399] rounded-full transition-all"
                style={{ width: `${(total_pips_win / (total_pips_win + total_pips_loss || 1)) * 100}%` }}
              />
            </div>
          </div>
          
          <div className="bg-[hsl(var(--surface-elevated))] rounded-lg p-4 border border-[hsl(var(--border))]">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm text-white font-medium">Pips Lost</span>
              <span className="text-lg font-bold text-[#E63946]">-{total_pips_loss}</span>
            </div>
            <div className="h-2 bg-[hsl(var(--border))] rounded-full overflow-hidden">
              <div 
                className="h-full bg-gradient-to-r from-[#E63946] to-[#F87171] rounded-full transition-all"
                style={{ width: `${(total_pips_loss / (total_pips_win + total_pips_loss || 1)) * 100}%` }}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default SignalMetrics;
